/**
 * Modal shown when a Free quota is exhausted (analysis, download, ES/EN) or AI design is requested.
 * Authenticated users go straight to Stripe Checkout so they can keep going.
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { trackGaEvent } from '../utils/analytics';
import { startStripeCheckout } from '../utils/checkout';
import './UpgradePrompt.css';

const REASON_KEYS = {
  plan_limit_design: {
    title: 'upgrade.designTitle',
    body: 'upgrade.designBody',
    points: ['upgrade.designPoint1', 'upgrade.designPoint2'],
  },
  plan_limit_studio: {
    title: 'upgrade.studioTitle',
    body: 'upgrade.studioBody',
    points: ['upgrade.studioPoint1', 'upgrade.studioPoint2'],
  },
  plan_limit_analysis: {
    title: 'upgrade.analysisTitle',
    body: 'upgrade.analysisBody',
    points: ['upgrade.analysisPoint1'],
  },
  plan_limit_download: {
    title: 'upgrade.downloadTitle',
    body: 'upgrade.downloadBody',
    points: ['upgrade.downloadPoint1', 'upgrade.downloadPoint2'],
  },
  plan_limit_localize: {
    title: 'upgrade.localizeTitle',
    body: 'upgrade.localizeBody',
    points: ['upgrade.localizePoint1', 'upgrade.localizePoint2'],
  },
};

const UpgradePrompt = ({
  open = false,
  errorCode = '',
  rewriteCvId = '',
  rewriteJob = '',
  onClose,
}) => {
  const { t } = useLanguage();
  const { isAuthenticated, isPro } = useAuth();
  const navigate = useNavigate();
  const closeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const fromCv = rewriteCvId ? String(rewriteCvId) : '';
  const job = (rewriteJob || '').trim();
  const keys = REASON_KEYS[errorCode] || {
    title: 'upgrade.genericTitle',
    body: 'upgrade.genericBody',
    points: [],
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    trackGaEvent('upgrade_prompt_shown', { reason: errorCode || 'unknown' });
    const timer = window.setTimeout(() => closeRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, errorCode]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const startCheckout = async (kind) => {
    trackGaEvent('upgrade_prompt_click', { reason: errorCode || 'unknown', kind });
    if (isPro) {
      onClose();
      return;
    }
    if (!isAuthenticated) {
      navigate('/pricing');
      return;
    }
    setLoading(true);
    try {
      await startStripeCheckout({
        kind,
        returnPath: window.location.pathname,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || t('pricing.checkoutError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => startCheckout('pro_intro');
  const handleOneshot = () => startCheckout('oneshot_pdf');
  const handleAts = () => startCheckout('oneshot_ats');
  const handleRewrite = () => {
    trackGaEvent('upgrade_prompt_click', {
      reason: errorCode || 'unknown',
      kind: 'rewrite',
    });
    const params = new URLSearchParams();
    if (fromCv) {
      params.set('fromCv', fromCv);
      params.set('autostart', '1');
    }
    if (job) {
      params.set('job', job);
    }
    const search = params.toString() ? `?${params.toString()}` : '';
    navigate(`/dashboard/quick-edit${search}`, {
      state: {
        ...(job ? { jobPosition: job } : {}),
        ...(fromCv ? { fromCv } : {}),
      },
    });
  };

  if (!open) {
    return null;
  }

  const isDownload = errorCode === 'plan_limit_download';
  const isAnalysis = errorCode === 'plan_limit_analysis';
  const showOneshotPrimary = isDownload || isAnalysis;
  const heroAmountKey = isDownload
    ? 'upgrade.oneshotAmount'
    : isAnalysis
      ? 'upgrade.atsAmount'
      : 'upgrade.introAmount';
  const heroPeriodKey = isDownload
    ? 'upgrade.oneshotPeriod'
    : isAnalysis
      ? 'upgrade.atsPeriod'
      : 'upgrade.introPeriod';
  const hintKey = isDownload
    ? 'upgrade.priceCancelDownload'
    : isAnalysis
      ? 'upgrade.priceCancelAnalysis'
      : 'upgrade.priceCancel';

  return (
    <div className="upgrade-prompt" role="presentation">
      <button
        type="button"
        className="upgrade-prompt__backdrop"
        aria-label={t('upgrade.close')}
        onClick={onClose}
      />
      <div
        className="upgrade-prompt__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-prompt-title"
        aria-describedby="upgrade-prompt-body"
      >
        <button
          type="button"
          className="upgrade-prompt__close"
          ref={closeRef}
          onClick={onClose}
          aria-label={t('upgrade.close')}
        >
          ×
        </button>
        <p className="upgrade-prompt__badge">
          {t(showOneshotPrimary ? 'upgrade.badgeOnce' : 'upgrade.badge')}
        </p>
        <h2 id="upgrade-prompt-title">{t(keys.title)}</h2>
        <p className="upgrade-prompt__body" id="upgrade-prompt-body">
          {t(keys.body)}
        </p>
        {keys.points?.length ? (
          <ul className="upgrade-prompt__points">
            {keys.points.map((pointKey) => (
              <li key={pointKey}>{t(pointKey)}</li>
            ))}
          </ul>
        ) : null}
        <p className="upgrade-prompt__price">
          <span className="upgrade-prompt__price-value">
            {t(heroAmountKey)}
          </span>
          <span className="upgrade-prompt__price-period">
            {t(heroPeriodKey)}
          </span>
        </p>
        <p className="upgrade-prompt__hint">{t(hintKey)}</p>
        <div className="upgrade-prompt__actions">
          {isDownload ? (
            <button
              type="button"
              className="btn btn-primary upgrade-prompt__cta"
              onClick={handleOneshot}
              disabled={loading}
            >
              {loading ? t('pricing.upgrading') : t('upgrade.oneshotCta')}
            </button>
          ) : null}
          {isAnalysis ? (
            <button
              type="button"
              className="btn btn-primary upgrade-prompt__cta"
              onClick={handleAts}
              disabled={loading}
            >
              {loading ? t('pricing.upgrading') : t('upgrade.atsCta')}
            </button>
          ) : null}
          {isAnalysis ? (
            <button
              type="button"
              className="upgrade-prompt__oneshot"
              onClick={handleRewrite}
              disabled={loading}
            >
              {t('upgrade.rewriteCta')}
            </button>
          ) : null}
          <button
            type="button"
            className={
              showOneshotPrimary
                ? 'upgrade-prompt__oneshot'
                : 'btn btn-primary upgrade-prompt__cta'
            }
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? t('pricing.upgrading') : t('upgrade.cta')}
          </button>
          <button
            type="button"
            className="upgrade-prompt__later"
            onClick={onClose}
          >
            {t('upgrade.later')}
          </button>
        </div>
      </div>
    </div>
  );
};

UpgradePrompt.propTypes = {
  open: PropTypes.bool,
  errorCode: PropTypes.string,
  rewriteCvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rewriteJob: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default UpgradePrompt;
